import React, { useState, useEffect } from 'react';

import _ from 'lodash';
import { Divider, Spinner, Typography } from '@telus-uds/components-web';

import request from '../../utils/request';

import { ContentWrapper, Box } from '../../design/css';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import { orderStatus } from '../../utils/constant';
import { getFormattedDate } from '../../utils/utils';

import Table from './Table';
import Pagination from './Pagination';
import SearchBar from './components/searchBar';
import Filters from './components/filters';
import Analytics from './components/analytics';
import ExportReport from './components/exportReport';

const IotOrderFulfillmentDashboard = () => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    srtDashboard: { noOrders }
  } = localizedStrings;
  const [ordersList, setOrdersList] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [pageLoading, setPageLoading] = useState();
  const [pageNumber, setPageNumber] = useState(1);
  const [filters, setFilters] = useState({
    iotProductSelected: '',
    status: '',
    flag: '',
    assignedUserId: '',
    createdBy: '',
    province: '',
    channelId: '',
    filterCriteria: '',
    endDateOpenDate: '',
    endDateSubmissionDate: '',
    startDateOpenDate: '',
    startDateSubmissionDate: ''
  });
  const ORDER_PER_PAGE = 30;

  const {
    pendingValidation,
    waitingOnCredit,
    waitingOnSales,
    contractSigned,
    contractSent,
    orderCanceled,
    contractExpired,
    orderInstalled,
    orderPlaced,
    orderActivated,
    orderShipped,
    orderCompleted
  } = orderStatus;

  const getPayload = () => {
    const payload = {
      ...filters,
      pageNumber,
      pageSize: ORDER_PER_PAGE,
      type: 'IOT',
      status: filters.status || [
        pendingValidation,
        waitingOnCredit,
        waitingOnSales,
        contractSigned,
        contractSent,
        orderCanceled,
        contractExpired,
        orderInstalled,
        orderPlaced,
        orderActivated,
        orderShipped,
        orderCompleted
      ]
    };

    if (filters.iotProductSelected)
      payload.iotProductSelected = filters.iotProductSelected.map(
        row => row.value
      );
    if (filters.status) payload.status = filters.status.map(row => row.value);
    if (filters.flag) payload.flag = filters.flag.map(row => row.value);
    if (filters.assignedUserId)
      payload.assignedUserId = filters.assignedUserId.map(row => row.value);
    if (filters.createdBy)
      payload.createdBy = filters.createdBy.map(row => row.value);
    if (filters.province)
      payload.province = filters.province.map(row => row.value);
    if (filters.channelId)
      payload.channelId = filters.channelId.map(row => row.value);
    if (filters.dealership)
      payload.dealership = filters.dealership.map(row => row.value);
    if (filters.startDateOpenDate)
      payload.startDate = getFormattedDate(filters.startDateOpenDate);

    if (filters.endDateOpenDate)
      payload.endDate = getFormattedDate(filters.endDateOpenDate);

    if (filters.startDateSubmissionDate) {
      payload.startDate = getFormattedDate(filters.startDateSubmissionDate);
    }
    if (filters.endDateSubmissionDate)
      payload.endDate = getFormattedDate(filters.endDateSubmissionDate);

    if (filters.startDateOpenDate || filters.endDateOpenDate)
      payload.filterCriteria = 'createdAt';

    if (filters.startDateSubmissionDate || filters.endDateSubmissionDate)
      payload.filterCriteria = 'validatedAt';

    delete payload.startDateOpenDate;
    delete payload.endDateOpenDate;
    delete payload.endDateSubmissionDate;
    delete payload.startDateSubmissionDate;

    // SOF-3356 T037 — broaden IoT OFD listing to the SM's full dealership
    // when dealership-wide visibility is enabled. Backend re-checks the
    // requester and silently ignores the field otherwise.
    const smProfile = request.getProfile();
    if (
      smProfile &&
      _.get(smProfile, 'role.name') === 'SALES_MANAGER' &&
      smProfile.dealershipVisibilityEnabled === true
    ) {
      const smDealershipName = _.get(smProfile, 'Dealership.name', '');
      if (smDealershipName) {
        payload.dealershipName = smDealershipName;
      }
    }

    return payload;
  };

  const setOrders = data => {
    setOrdersList(_.get(data, 'data.results', []));
    setTotalOrders(_.get(data, 'data.totalCount', 0));
  };

  const fetchOrders = () => {
    setPageLoading(true);
    request
      .post('/orders/filter', getPayload())
      .then(response => {
        setOrders(response);
        setPageLoading(false);
      })
      .catch(() => {
        setPageLoading(false);
      });
  };

  useEffect(() => {
    fetchOrders();
  }, [pageNumber]);

  return (
    <>
    <Divider space={1} variant={{ inverse: true }} />
      <ContentWrapper style={{ maxWidth: '92%', margin: '0 auto', padding: 0 }}>
        <Box>
          <Analytics />
          <Filters
            setOrders={setOrders}
            filters={filters}
            setFilters={setFilters}
            getPayload={getPayload}
            includeIncompleteStatus={false}
            setPageNumber={setPageNumber}
          />
          <Divider space={1} variant={{ inverse: true }} />
          <SearchBar setOrders={setOrders} fetchOrders={fetchOrders} />
          <>
            {pageLoading ? (
              <Spinner show label="Loading Orders..." />
            ) : (
              <div className="table-container-main">
                {!pageLoading && ordersList.length === 0 ? (
                  <Typography heading="h3" variant={{ size: 'h3' }} block>{noOrders}</Typography>
                ) : (
                  <>
                    <div
                      className="table-wrapper"
                      style={{
                        border: '1px solid #e3e3e3'
                      }}
                    >
                      <Table
                        data={ordersList}
                        fetchOrders={fetchOrders}
                        totalOrders={totalOrders}
                      />
                    </div>

                    <Pagination
                      ordersPerPage={ORDER_PER_PAGE}
                      totalOrders={totalOrders}
                      pageNumber={pageNumber}
                      setPageNumber={setPageNumber}
                    />
                    <ExportReport getPayload={getPayload} />
                  </>
                )}
              </div>
            )}
          </>
        </Box>
      </ContentWrapper>
    </>
  );
};

export default IotOrderFulfillmentDashboard;
